// «Enseñarle»: una frase, y el agente se acuerda (4.2, capacidad 1).
//
// El cliente escribe «no des precio de los chalés de Somió por WhatsApp,
// ofrece visita» y ve lo que el agente va a recordar, con sus palabras. Si no
// es eso, lo corrige. Y confirma.
//
// **En ningun momento elige un tipo.** Ese es el punto entero de D1: cinco
// tipos en el motor, cero categorias en pantalla. El tipo lo propone el
// servidor y aqui solo se enseña como se llama -«Una corrección que le
// hiciste»-, para que el cliente entienda que peso va a tener, no para que
// elija.
//
// **Dos pasos y no uno.** Proponer y guardar son dos llamadas con un si en
// medio: si se guardara directo, corregir el texto significaria editar una
// memoria que el agente ya esta usando.
//
// **La linea es editable y la frase no.** Lo que se guarda es la linea, porque
// es lo que el agente leera en cada turno; la frase era solo la forma de
// contarlo.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  workspacesService,
  type MemoriaPropuesta,
} from '@/services/workspacesService'

export function TeachTheAgent({ onGuardada }: { onGuardada: () => void }) {
  const { t } = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const [frase, setFrase] = useState('')
  const [propuesta, setPropuesta] = useState<MemoriaPropuesta | null>(null)
  const [linea, setLinea] = useState('')
  const [aviso, setAviso] = useState('')
  const [trabajando, setTrabajando] = useState(false)

  const empezarDeCero = () => {
    setFrase('')
    setPropuesta(null)
    setLinea('')
    setAviso('')
  }

  const cerrar = (abrir: boolean) => {
    setAbierto(abrir)
    if (!abrir) empezarDeCero()
  }

  const askForAProposal = async () => {
    if (!frase.trim()) return
    setTrabajando(true)
    setAviso('')
    try {
      const { propuesta: salida, queDecir } = await workspacesService.proponerMemoria(frase)
      if (!salida) {
        // No es un error: abstenerse es mejor que forzar una memoria, y el
        // servidor manda lo que hay que contestarle.
        setAviso(queDecir || t('knowledge.ensenarle.no_entendido'))
        return
      }
      setPropuesta(salida)
      setLinea(salida.linea)
      if (salida.falta) setAviso(t('knowledge.ensenarle.falta', { falta: salida.falta }))
    } catch {
      setAviso(t('knowledge.ensenarle.error'))
    } finally {
      setTrabajando(false)
    }
  }

  const save = async () => {
    if (!propuesta || !linea.trim()) return
    setTrabajando(true)
    try {
      await workspacesService.guardarMemoria({
        propuesta,
        linea: linea.trim(),
        quienLaEscribio: t('knowledge.ensenarle.firma'),
      })
      cerrar(false)
      onGuardada()
    } catch {
      setAviso(t('knowledge.ensenarle.error'))
    } finally {
      setTrabajando(false)
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={cerrar}>
      <Button variant="default" size="sm" onClick={() => setAbierto(true)}>
        <Plus className="mr-2 h-4 w-4" />
        {t('knowledge.ensenarle.boton')}
      </Button>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('knowledge.ensenarle.titulo')}</DialogTitle>
          <DialogDescription>{t('knowledge.ensenarle.explicacion')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!propuesta && (
            <div className="space-y-2">
              <Label id="ensenarle-frase-etiqueta" htmlFor="ensenarle-frase">
                {t('knowledge.ensenarle.etiqueta')}
              </Label>
              <Textarea
                id="ensenarle-frase"
                // `htmlFor`/`id` ya lo asocia; el linter no ve a traves de
                // nuestro `Label`, asi que se le dice explicito.
                aria-labelledby="ensenarle-frase-etiqueta"
                value={frase}
                onChange={(e) => setFrase(e.target.value)}
                placeholder={t('knowledge.ensenarle.ejemplo')}
                rows={3}
              />
            </div>
          )}

          {propuesta && (
            <div className="space-y-2">
              <Label id="ensenarle-linea-etiqueta" htmlFor="ensenarle-linea">
                {t('knowledge.ensenarle.esto_recordare')}
              </Label>
              <Textarea
                id="ensenarle-linea"
                aria-labelledby="ensenarle-linea-etiqueta"
                value={linea}
                onChange={(e) => setLinea(e.target.value)}
                rows={3}
              />
              {/* El nombre del tipo se enseña para que se entienda que peso va
                  a tener, no para elegirlo. La clave no se pinta nunca. */}
              <p className="text-xs text-muted-foreground">{propuesta.seLlama}</p>
            </div>
          )}

          {aviso && <p className="text-sm text-muted-foreground">{aviso}</p>}

          <div className="flex justify-end gap-2">
            {propuesta && (
              <Button variant="ghost" size="sm" onClick={empezarDeCero} disabled={trabajando}>
                {t('knowledge.ensenarle.otra_vez')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={propuesta ? save : askForAProposal}
              disabled={
                trabajando ||
                (propuesta ? !linea.trim() || !propuesta.sePuedeGuardar : !frase.trim())
              }
            >
              {propuesta ? t('knowledge.ensenarle.confirmar') : t('knowledge.ensenarle.ver')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
